import { SETTING_PANEL_TABS } from '@/constants/settingPanels';
import { getObjectByCode } from '@/threejs/objectManager';
import eventBus from '@/utils/eventBus';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useSelectionStore = defineStore('selection', () => {
  const selectedObjectId = ref(null);
  const selectedMeshId = ref(null); // New: for tracking selected mesh within a model
  const selectedObjects = ref([]);
  const selectedObjectTransformType = ref(null);
  const selectedObjectTransformWorld = ref('world');
  const selectedObjectTransformTarget = ref(false);

  const selectedObjectsWithChildren = computed(() => {
    const objects = [];

    selectedObjects.value.forEach((object) => {
      objects.push(object);

      // Add all children recursively
      object.traverse((child) => {
        if (child !== object) {
          objects.push(child);
        }
      });
    });

    return objects;
  });

  const setSelectedObject = (id, options = {}) => {
    const { targetMode = false } = options;
    // Deselect previous object if any
    if (selectedObjectId.value) {
      const previousMesh = getObjectByCode(selectedObjectId.value);
      if (previousMesh) {
        previousMesh.clearSelection();
        delete previousMesh.userData.selected;
      }
    }
    // Select new object
    selectedObjectId.value = id;
    selectedMeshId.value = null; // Clear mesh selection when selecting a new object
    selectedObjects.value = [];
    // Set target transform state (defaults to false unless specified)
    selectedObjectTransformTarget.value = targetMode;

    if (id) {
      const mesh = getObjectByCode(id);
      if (mesh) {
        mesh.userData.selected = true;
        if (mesh && mesh.select) mesh.select();
        selectedObjects.value = [mesh];

        // If the selected mesh is an annotation, trigger the annotations tab

        if (mesh.userData.type === 'annotation') {
          eventBus.emit('selectRightPanelTab', SETTING_PANEL_TABS.ANNOTATIONS);
        } else if (mesh.userData.type === 'measurement') {
          eventBus.emit('selectRightPanelTab', SETTING_PANEL_TABS.MEASUREMENTS);
        } else {
          eventBus.emit('selectRightPanelTab', SETTING_PANEL_TABS.TREE_VIEW);
        }
      }
    }

    // Trigger render to update projector helper lines and labels
    window.dispatchEvent(new Event('render'));
  };

  const setSelectedMesh = (meshId, objectId = null) => {
    if (!meshId) {
      selectedMeshId.value = null;
      return;
    }
    setSelectedObject(objectId);
    selectedMeshId.value = meshId;
  };

  const clearSelection = () => {
    setSelectedMesh(null);
    setSelectedObject(null);
  };

  return {
    selectedObjectId,
    selectedMeshId,
    selectedObjects,
    selectedObjectsWithChildren,
    selectedObjectTransformType,
    selectedObjectTransformWorld,
    selectedObjectTransformTarget,
    setSelectedObject,
    setSelectedMesh,
    clearSelection,
  };
});
